import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet';
import RoleBasedNavigation from '../../components/navigation/RoleBasedNavigation';
import BreadcrumbTrail from '../../components/navigation/BreadcrumbTrail';
import Icon from '../../components/AppIcon';
import ValidationStatusCards from './components/ValidationStatusCards';
import ValidationTable from './components/ValidationTable';
import FilterBar from './components/FilterBar';
import AudioPlaybackModal from './components/AudioPlaybackModal';
import { supabase } from '../../lib/supabase';
import { getAllPlays } from '../../services/playsService';

export default function AudioQualityValidationDashboard() {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [plays, setPlays] = useState([]);
  const [audioData, setAudioData] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [statistics, setStatistics] = useState({
    totalFiles: 0,
    qualityPassed: 0,
    qualityFailed: 0,
    durationIssues: 0,
    uploadComplete: 0,
    uploadPending: 0
  });
  
  const [filters, setFilters] = useState({
    playId: '',
    validationStatus: 'all',
    qualityIssue: 'all',
    searchQuery: ''
  });

  const [selectedAudio, setSelectedAudio] = useState(null);
  const [showPlaybackModal, setShowPlaybackModal] = useState(false);

  useEffect(() => {
    loadDashboardData();
  }, []);

  useEffect(() => {
    applyFilters();
  }, [filters, audioData]);

  const loadDashboardData = async () => {
    try {
      setLoading(true);
      setError(null);

      // Fetch plays
      const playsData = await getAllPlays();
      setPlays(playsData || []);

      // Fetch all audio recordings with line details
      const { data: recordings, error: recordingsError } = await supabase?.from('audio_recordings')?.select(`
          id,
          line_id,
          audio_url,
          duration_seconds,
          created_at,
          lines!inner(
            line_id,
            play_id,
            character,
            dialogue,
            plays!inner(
              title
            )
          )
        `)?.order('created_at', { ascending: false });

      if (recordingsError) throw recordingsError;

      // Process recordings and calculate quality metrics
      const processedData = recordings?.map(recording => {
        const duration = recording?.duration_seconds || 0;
        const hasAudio = !!recording?.audio_url;
        
        // Quality checks
        const durationValid = duration > 0 && duration < 300; // 0-5 minutes
        const qualityScore = calculateQualityScore(recording);
        const validationStatus = qualityScore >= 70 ? 'pass' : 'fail';
        
        return {
          id: recording?.id,
          lineId: recording?.lines?.line_id,
          playTitle: recording?.lines?.plays?.title,
          playId: recording?.lines?.play_id,
          character: recording?.lines?.character,
          dialogue: recording?.lines?.dialogue,
          audioUrl: recording?.audio_url,
          duration: duration,
          durationValid,
          qualityScore,
          validationStatus,
          uploadStatus: hasAudio ? 'complete' : 'pending',
          createdAt: recording?.created_at
        };
      }) || [];

      setAudioData(processedData);
      setFilteredData(processedData);

      // Calculate statistics
      const stats = {
        totalFiles: processedData?.length,
        qualityPassed: processedData?.filter(d => d?.validationStatus === 'pass')?.length,
        qualityFailed: processedData?.filter(d => d?.validationStatus === 'fail')?.length,
        durationIssues: processedData?.filter(d => !d?.durationValid)?.length,
        uploadComplete: processedData?.filter(d => d?.uploadStatus === 'complete')?.length,
        uploadPending: processedData?.filter(d => d?.uploadStatus === 'pending')?.length
      };

      setStatistics(stats);

    } catch (err) {
      console.error('Error loading validation data:', err);
      setError(err?.message || 'Failed to load validation data');
    } finally {
      setLoading(false);
    }
  };

  const calculateQualityScore = (recording) => {
    let score = 100;
    const duration = recording?.duration_seconds || 0;

    // Duration checks
    if (duration === 0) score -= 50;
    else if (duration < 1) score -= 30;
    else if (duration > 300) score -= 20;

    // Audio URL check
    if (!recording?.audio_url) score -= 50;

    return Math.max(0, score);
  };

  const applyFilters = () => {
    let filtered = [...audioData];

    // Play filter
    if (filters?.playId) {
      filtered = filtered?.filter(d => d?.playId === filters?.playId);
    }

    // Validation status filter
    if (filters?.validationStatus !== 'all') {
      filtered = filtered?.filter(d => d?.validationStatus === filters?.validationStatus);
    }

    // Quality issue filter
    if (filters?.qualityIssue === 'duration') {
      filtered = filtered?.filter(d => !d?.durationValid);
    } else if (filters?.qualityIssue === 'missing') {
      filtered = filtered?.filter(d => !d?.audioUrl);
    } else if (filters?.qualityIssue === 'low_quality') {
      filtered = filtered?.filter(d => d?.qualityScore < 70);
    }

    // Search filter
    if (filters?.searchQuery) {
      const query = filters?.searchQuery?.toLowerCase();
      filtered = filtered?.filter(d => 
        d?.lineId?.toLowerCase()?.includes(query) ||
        d?.character?.toLowerCase()?.includes(query) ||
        d?.dialogue?.toLowerCase()?.includes(query)
      );
    }

    setFilteredData(filtered);
  };

  const handlePlayAudio = (audioItem) => {
    setSelectedAudio(audioItem);
    setShowPlaybackModal(true);
  };

  const handleApprove = async (audioId) => {
    // In a real implementation, you would update a validation status field
    console.log('Approved audio:', audioId);
    // Reload data
    await loadDashboardData();
  };

  const handleReject = async (audioId) => {
    // In a real implementation, you would update a validation status field
    console.log('Rejected audio:', audioId);
    // Reload data
    await loadDashboardData();
  };

  const breadcrumbItems = [
    { label: 'Admin Dashboard', path: '/admin-dashboard' },
    { label: 'Audio Production', path: '/audio-production-dashboard' },
    { label: 'Quality Validation', path: '/audio-quality-validation-dashboard' }
  ];

  return (
    <div className="min-h-screen bg-background">
      <Helmet>
        <title>Audio Quality Validation Dashboard - BardPlay</title>
      </Helmet>

      <RoleBasedNavigation />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <BreadcrumbTrail items={breadcrumbItems} />

        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center gap-3 mb-2">
            <div className="w-12 h-12 rounded-lg bg-gradient-to-br from-purple-500 to-purple-600 flex items-center justify-center">
              <Icon name="CheckCircle2" size={24} className="text-white" />
            </div>
            <div>
              <h1 className="text-3xl font-bold text-foreground">Audio Quality Validation</h1>
              <p className="text-muted-foreground">Comprehensive quality assurance for audio recordings</p>
            </div>
          </div>
        </div>

        {/* Loading State */}
        {loading && (
          <div className="flex items-center justify-center py-12">
            <Icon name="Loader2" size={32} className="animate-spin text-primary" />
            <span className="ml-3 text-lg text-muted-foreground">Loading validation data...</span>
          </div>
        )}

        {/* Error State */}
        {error && (
          <div className="bg-destructive/10 border border-destructive rounded-lg p-4 mb-6">
            <div className="flex items-center gap-2">
              <Icon name="AlertCircle" size={20} className="text-destructive" />
              <p className="text-destructive font-medium">{error}</p>
            </div>
          </div>
        )}

        {/* Content */}
        {!loading && !error && (
          <>
            {/* Validation Status Cards */}
            <ValidationStatusCards statistics={statistics} />

            {/* Filter Bar */}
            <FilterBar 
              plays={plays}
              filters={filters}
              onFilterChange={setFilters}
            />

            {/* Validation Table */}
            <ValidationTable 
              data={filteredData}
              onPlayAudio={handlePlayAudio}
              onApprove={handleApprove}
              onReject={handleReject}
            />
          </>
        )}
      </div>

      {/* Audio Playback Modal */}
      {showPlaybackModal && selectedAudio && (
        <AudioPlaybackModal 
          audio={selectedAudio}
          onClose={() => setShowPlaybackModal(false)}
        />
      )}
    </div>
  );
}
